import type { HTMLAttributes } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import type { TypographyProps } from '@elfsight/widget-ui';
import { createReadableClass } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import type { PropsWithClassName } from '../../../../types';
import { usePlayer } from '../../player';
import type { ExtendedAudioItem } from '../../sources';
import { TrackInfoContent } from './TrackInfoContent';

interface TrackInfoProps
  extends HTMLAttributes<HTMLDivElement>,
    PropsWithClassName {
  disableTicker?: boolean;
  wrapTitle?: boolean;
  track?: ExtendedAudioItem;
  titleTextStyle?: TypographyProps['textStyle'];
  authorTextStyle?: TypographyProps['textStyle'];
}

export function TrackInfo({
  track,
  wrapTitle,
  disableTicker = false,
  titleTextStyle = {
    fontSize: 15,
    lineHeight: '20px',
    fontWeight: 'bold'
  },
  authorTextStyle = {
    fontSize: 13,
    lineHeight: '16px'
  },
  ...forwardingProps
}: TrackInfoProps) {
  const { playerParts } = useSettings();
  const { currentItem } = usePlayer();

  const intersectionObserverRef = useRef<IntersectionObserver | null>(null);

  const [isVisible, setIsVisible] = useState(false);
  const handleIntersection = useCallback(
    ([{ isIntersecting }]: IntersectionObserverEntry[]) => {
      setIsVisible(isIntersecting);
    },
    []
  );

  const refCallback = useCallback(
    (node: HTMLDivElement) => {
      intersectionObserverRef.current?.disconnect();

      if (!node) {
        return;
      }

      intersectionObserverRef.current = new IntersectionObserver(
        handleIntersection,
        { threshold: 0.4 }
      );
      intersectionObserverRef.current?.observe(node);
    },
    [handleIntersection]
  );

  const { hasTitle, hasCaption, hasAuthor } = track ?? currentItem ?? {};

  const hasCaptionOrAuthor = hasCaption || hasAuthor;
  if (!hasTitle && !hasCaptionOrAuthor && !playerParts.includes('status')) {
    return null;
  }

  const shouldDisableTicker = disableTicker || !isVisible;

  return (
    <Container ref={refCallback} {...forwardingProps}>
      <TrackInfoContent
        track={track}
        wrapTitle={wrapTitle}
        titleTextStyle={titleTextStyle}
        authorTextStyle={authorTextStyle}
        disableTicker={shouldDisableTicker}
        isVisible={isVisible}
      />
    </Container>
  );
}

const Container = styled.div.attrs({
  className: createReadableClass({
    component: 'track-info',
    element: 'container'
  })
})`
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: end;
`;
